<template>
  <div class="bet-input">
    <div class="inputs">

      <!-- Amount -->
      <label>
        <img src="@/assets/img/trade/wallet.svg" class="coin-small2" />
        {{ $t('turbo.amount') }}
      </label>

      <div class="amount-input">
        <img :src="coinIcon" alt="coin" class="coin-icon" />
        <input type="number" v-model="amount" min="1" />
        <span class="fiat-symbol">{{ fiatSymbol }}</span>
      </div>

      <!-- Quick action buttons -->
      <div class="amount-actions">
        <button @click="halveAmount">
          <img src="@/assets/img/trade/half.svg" alt="½" class="action-icon" />
        </button>
        <button @click="doubleAmount">
          <img src="@/assets/img/trade/double.svg" alt="2x" class="action-icon" />
        </button>
        <button @click="increaseAmount">
          <img src="@/assets/img/trade/up2.svg" alt="↑" class="action-icon" />
        </button>
        <button @click="decreaseAmount">
          <img src="@/assets/img/trade/down2.svg" alt="↓" class="action-icon" />
        </button>
      </div>

      <!-- Profit / Loss -->
      <label class="duration-label">
        <img src="@/assets/img/trade/win.svg" class="coin-small2" />
        {{ $t('turbo.profitLoss') }}
      </label>

      <div class="profit-loss">
        <div class="item gain">
          <img src="@/assets/img/trade/up.svg" alt="up" />
          <span>+{{ potentialGain }}{{ fiatSymbol }}</span>
        </div>
        <div class="item loss">
          <img src="@/assets/img/trade/down.svg" alt="down" />
          <span>{{ potentialLoss }}{{ fiatSymbol }}</span>
        </div>
      </div>

      <!-- Duration -->
      <div class="duration-dropdown" :class="{ open: showDropdown }">
        <label class="duration-label">
          <img src="@/assets/img/trade/clock.svg" class="coin-small2" />
          {{ $t('turbo.duration') }}
        </label>

        <!-- Selected -->
        <div class="dropdown-selected" @click="toggleDropdown">
          <img src="@/assets/img/trade/time.svg" class="coin-small2" />
          <span>{{ selectedDuration.value }}s — {{ selectedDuration.payout }}%</span>
          <img src="@/assets/img/trade/arrow.svg" class="dropdown-arrow" :class="{ open: showDropdown }" alt="arrow"/>
        </div>

        <!-- Desktop Dropdown -->
        <div v-if="showDropdown && !isMobile" class="dropdown-options desktop">
          <ul>
            <li v-for="d in durations" :key="d.value" @click="selectDuration(d)" :class="{ active: d.value === duration.value }">
              <img src="@/assets/img/trade/time.svg" class="coin-small2" />
              <span>{{ d.value }} {{ $t('turbo.seconds') }}</span>
              <small>{{ d.payout }}%</small>
            </li>
          </ul>
        </div>

        <!-- Mobile Bottom Sheet -->
        <div v-if="showDropdown && isMobile" class="dropdown-bottomsheet">
          <div class="sheet-header">
            <span>{{ $t('turbo.selectDuration') }}</span>
            <button @click="showDropdown=false">✕</button>
          </div>
          <ul>
            <li v-for="d in durations" :key="d.value" @click="selectDuration(d)" :class="{ active: d.value === duration.value }">
              <img src="@/assets/img/trade/time.svg" class="coin-small2" />
              <span>{{ d.value }} {{ $t('turbo.seconds') }}</span>
              <small>{{ d.payout }}%</small>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Info -->
    <div class="info">
      <p>
        <strong>{{ $t('turbo.currentPrice') }}:</strong> {{ fmt(currentPrice) }}
        <img :src="chartCoinIcon" class="coin-small2" />
      </p>
      <p>
        <strong>{{ $t('turbo.entryPrice') }}:</strong> {{ fmt(entryPrice) }}
        <img :src="chartCoinIcon" class="coin-small2" />
      </p>
      <p>
        <strong>{{ $t('turbo.duration') }}:</strong> {{ duration }}s
        <img src="@/assets/img/trade/time.svg" class="coin-small2" />
      </p>
    </div>

    <!-- Action Buttons -->
    <div class="actions">
      <div class="btn-wrapper">
        <button class="long" @click="openTrade('UP')" :disabled="isLoading">
          <img src="@/assets/img/trade/long.svg" class="btn-icon" /> {{ $t('turbo.up') }}
        </button>
        <div v-if="isLoading" class="loading-overlay">
          <img src="@/assets/img/trade/loading.svg" class="loading-spinner" />
        </div>
      </div>

      <div class="btn-wrapper">
        <button class="short" @click="openTrade('DOWN')" :disabled="isLoading">
          <img src="@/assets/img/trade/short.svg" class="btn-icon" /> {{ $t('turbo.down') }}
        </button>
        <div v-if="isLoading" class="loading-overlay">
          <img src="@/assets/img/trade/loading.svg" class="loading-spinner" />
        </div>
      </div>
    </div>

    <!-- Progress bar -->
    <div v-if="countdown > 0" class="linear-progress">
      <div class="bar" :style="{ width: progress + '%' }"></div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <h2>{{ $t('turbo.turboTrade') }}</h2>

        <!-- Details -->
        <div class="details-grid">
          <div class="detail-item">
            <span class="label">{{ $t('turbo.symbol') }}</span>
            <span class="value">
              <img :src="chartCoinIcon" class="coin-small2" />
              {{ props.symbol }}
            </span>
          </div>

          <div class="detail-item">
            <span class="label">{{ $t('turbo.amount') }}</span>
            <span class="value">
              <img :src="chartCoinIcon" class="coin-small2" />
              {{ amount }} <span class="fiat-symbol">{{ fiatSymbol }}</span>
            </span>
          </div>

          <div class="detail-item">
            <span class="label">{{ $t('turbo.entry') }}</span>
            <span class="value">
              {{ fmt(entryPrice) }}
              <img :src="chartCoinIcon" class="coin-small2" />
            </span>
          </div>
        </div>

        <!-- Progress Circle -->
        <div class="circle-wrapper">
          <svg viewBox="0 0 36 36">
            <defs>
              <linearGradient id="circleGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#01e0a3" />
                <stop offset="100%" stop-color="#00aa6d" />
              </linearGradient>
            </defs>
            <path class="bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
            <path class="progress" :stroke-dasharray="progress + ', 100'" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
          </svg>
          <div class="countdown-center">{{ countdown }}s</div>
        </div>

        <!-- Result -->
        <div v-if="countdown === 0" class="result">
          <p v-if="tradeResult === 'win'" class="win">{{ $t('turbo.win') }} 🎉</p>
          <p v-else-if="tradeResult === 'lose'" class="lose">{{ $t('turbo.lose') }} ❌</p>
          <p v-else class="draw">{{ $t('turbo.draw') }} 🤝</p>
        </div>

        <button class="btn-close-modal" @click="closeModal">{{ $t('turbo.close') }}</button>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed  } from "vue"
import { useStore } from "vuex"

const props = defineProps({
  symbol: { type: String, required: true },
  socket: { type: Object, required: false },
  user: { type: Object, required: true },
})

const fiatMap = {
  TRY: "₺",
  USD: "$",
  EUR: "€",
  USDT: "₮",
  CNY: "¥",
  INR: "₹",
  IDR: "Rp"
}

const fiatSymbol = computed(() => {
  return fiatMap[props.user.currency.fiatCurrency] || props.user.currency.fiatCurrency
})

const coinIcon = computed(() => {
  const coin = props.user.currency.coinType.toUpperCase()
  return require(`@/assets/img/coins/${coin}.svg`)
})

function halveAmount() {
  if (amount.value > 1) {
    amount.value = Math.max(1, Math.floor(amount.value / 2))
  }
}

function doubleAmount() {
  amount.value = amount.value * 2
}

function increaseAmount() {
  amount.value = amount.value + 1
}

function decreaseAmount() {
  if (amount.value > 1) {
    amount.value = amount.value - 1
  }
}

const potentialGain = computed(() => {
  const d = durations.find(x => x.value === duration.value)
  if (!d) return 0
  return (amount.value * (d.payout / 100)).toFixed(2)
})

const potentialLoss = computed(() => {
  if (!amount.value) return 0
  return (-amount.value).toFixed(2)
})

function iconUrl(base) {
  return `https://cdn.jsdelivr.net/gh/vadimmalykhin/binance-icons/crypto/${base.toLowerCase()}.svg`
}

const showDropdown = ref(false)
const isMobile = ref(false)

const selectedDuration = computed(() => {
  return durations.find(x => x.value === duration.value) || durations[0]
})

function toggleDropdown() {
  showDropdown.value = !showDropdown.value
}

function selectDuration(d) {
  duration.value = d.value
  showDropdown.value = false
}

onMounted(() => {
  const checkMobile = () => {
    isMobile.value = window.innerWidth <= 768
  }
  checkMobile()
  window.addEventListener("resize", checkMobile)

  document.addEventListener("click", (e) => {
    if (!e.target.closest(".duration-dropdown")) {
      showDropdown.value = false
    }
  })
})


const amount = ref(10)
const duration = ref(30)
const durations = [
  { value: 15, payout: 90 },   
  { value: 30, payout: 100 },  
  { value: 60, payout: 110 },  
  { value: 90, payout: 120 },  
  { value: 120, payout: 130 },  
];

const entryPrice = ref(null)
const currentPrice = ref(null)
const isLoading = ref(true) // coin değiştiğinde true olacak

/* --- Countdown --- */
const countdown = ref(0)
const showModal = ref(false)
const tradeResult = ref(null)
const progress = ref(100)

let timer = null
let ws = null

const chartBase = computed(() => {
  if (!props.symbol) return ""
  // Binance sembollerinin son kısmı genelde quote currency olur (USDT, BUSD, USD, TRY vs.)
  return props.symbol.replace(/USDT|BUSD|USD|TRY|EUR|USDC$/i, "")
})

const chartCoinIcon = computed(() => {
  return iconUrl(chartBase.value)
})

/* --- Binance Price WS --- */
async function fetchInitialPrice(symbol) {
  try {
    const res = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol.toUpperCase()}`)
    const data = await res.json()
    currentPrice.value = parseFloat(data.price)
    isLoading.value = false  // REST yanıtı geldiğinde overlay kapanır
  } catch (err) {
    console.error("❌ REST fiyat alınamadı:", err)
  }
}

function connectWS() {
  if (ws) ws.close()
  isLoading.value = true // yeni coin yüklenirken overlay başlasın

  if (!props.symbol) return

  // 🔹 Önce REST'ten fiyatı çek
  fetchInitialPrice(props.symbol)

  const stream = props.symbol.toLowerCase() + "@trade"
  ws = new WebSocket(`wss://stream.binance.com:9443/ws/${stream}`)

  ws.onmessage = (ev) => {
    const data = JSON.parse(ev.data)
    currentPrice.value = parseFloat(data.p)

    // 🔹 Sadece ilk tick geldiğinde gerekirse overlay'i kapat
    if (isLoading.value) {
      isLoading.value = false
    }
  }
}

onMounted(connectWS)
onBeforeUnmount(() => { if (ws) ws.close() })
watch(() => props.symbol, connectWS)

const store = useStore()

function openTrade(dir) {
  if (!currentPrice.value) return
  entryPrice.value = currentPrice.value

  const payload = {
    token: store.getters.authToken,
    userId: props.user._id,
    symbol: props.symbol,
    amount: amount.value,
    direction: dir,
    duration: duration.value,
  }

  const socket = store.getters.socketGeneral
  socket.emit("turbo:open", payload, (res) => {
    if (res.success) {
      startCountdown()
      showModal.value = true
    } else {
      alert("Hata ❌: " + res.error)
    }
  })
}

function startCountdown() {
  countdown.value = duration.value
  progress.value = 100
  tradeResult.value = null

  if (timer) clearInterval(timer)
  timer = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value--
      progress.value = (countdown.value / duration.value) * 100
    } else {
      clearInterval(timer)
      tradeResult.value = Math.random() > 0.5 ? "win" : "lose"

      // ⏳ Kullanıcı kapatmazsa 2 saniye sonra otomatik kapat
      setTimeout(() => {
        if (showModal.value) {
          closeModal()
        }
      }, 2000)
    }
  }, 1000)
}


function closeModal() {
  showModal.value = false
}

function fmt(v) {
  if (v === null || v === undefined) return "—"
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(Number(v))
}
</script>

<style scoped>
.bet-input {
  background: var(--background3);
  padding: 0.8rem;
  border-radius: 7px;
  color: #f9fafb;
  max-width: 420px;
  margin: auto;
  height: 100%;
  box-shadow: 0 6px 18px rgba(0,0,0,0.3);
  font-family: "Inter", sans-serif;
}
.inputs label {
  display: block;
  margin-top: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #d1d5db;
  font-family: "Montserrat", Arial, "Segoe UI", Helvetica, sans-serif;
}
.inputs input,
.inputs select {
  width: 100%;
  padding: 8px 10px;
  
  border-radius: 5px;
  border: none;
  outline: none;
  background: var(--background2);
  color: #f3f4f6;
  font-family: "Montserrat", Arial, "Segoe UI", Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
 
  position: relative;
}

.info {
  margin-top: 20px;
  font-size: 14px;
  background: var(--background2);
  padding: 12px;
  border-radius: 8px;
      border: 1px solid #0f172a;
}
.info strong { color: #9ca3af; }

.amount-input {
  display: flex;
  align-items: center;
  background: var(--background2);
  border-radius: 6px;
  padding: 6px 10px;
  margin-top: 6px;
  position: relative;
      border: 1px solid #0f172a;
}
.amount-input input {
  flex: 1;
  border: none;
  background: transparent;
  color: #f3f4f6;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  padding: 6px;
}
.amount-input .coin-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
}
.amount-input .fiat-symbol {
  margin-left: 8px;
  font-weight: 700;
  font-size: 14px;
  color: #9ca3af;
}

.amount-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}
.amount-actions button {
  flex: 1;
  padding: 6px 0;
  border: none;
  border-radius: 6px;
  background: var(--background2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}
.amount-actions button:hover {
  background: #4b5563;
}
.amount-actions .action-icon {
  width: 20px;
  height: 20px;
}
.coin-small2 {
  width: 22px;
  height: 22px;
  margin-left: 1px; /* yazıdan sonra olacağı için left boşluk */
  vertical-align: middle;
}
.profit-loss {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  background: var(--background2);
  padding: 8px 12px;
  border-radius: 6px;
      border: 1px solid #0f172a;
}
.profit-loss .item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 14px;
}
.profit-loss .gain span { color: #22c55e; }
.profit-loss .loss span { color: #ef4444; }
.profit-loss img {
  width: 26px;
  height: 26px;
}

.duration-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #d1d5db;
  margin-bottom: 6px;
  
}



.duration-dropdown {
  position: relative;
  width: 100%;
  user-select: none;
}

.dropdown-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--background2);
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: #f3f4f6;
  position: relative;
      border: 1px solid #0f172a;
}

 

.dropdown-selected .arrow {
  margin-left: 8px;
  transition: transform 0.2s;
}
.duration-dropdown.open .arrow {
  transform: rotate(180deg);
}

/* --- Desktop Dropdown --- */
.dropdown-options.desktop {
  position: absolute;
  top: 110%;
  left: 0;
  right: 0;
  background: var(--background);
  border-radius: 8px;
  
  box-shadow: 0 6px 12px rgba(0,0,0,0.3);
  z-index: 10;
  max-height: 240px;
  overflow-y: auto;
}

.dropdown-options ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-options li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #f3f4f6;
  transition: background 0.2s;
}
.dropdown-options li small {
  color: #9ca3af;
}
.dropdown-options li.active {
  background: rgba(0,255,162,0.1);
  color: #01e0a3;
}
.dropdown-options li:hover {
  background: rgba(255,255,255,0.08);
}

/* --- Mobile Bottom Sheet --- */
.dropdown-bottomsheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--background);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.5);
  z-index: 999;
  animation: slideUp 0.25s ease;
  padding-bottom: 100px;
}

.sheet-header {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  color: #f3f4f6;
}
.sheet-header button {
  background: transparent;
  border: none;
  font-size: 18px;
  color: #f3f4f6;
  cursor: pointer;
}

.dropdown-bottomsheet ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dropdown-bottomsheet li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #f3f4f6;
  transition: background 0.2s;
}
.dropdown-bottomsheet li small {
  color: #9ca3af;
}
.dropdown-bottomsheet li.active {
  background: rgba(0,255,162,0.1);
  color: #01e0a3;
}
.dropdown-bottomsheet li:hover {
  background: rgba(255,255,255,0.08);
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.dropdown-arrow {
  width: 20px;
  height: 20px;
  margin-left: 8px;
  transition: transform 0.2s ease;
}
.dropdown-arrow.open {
  transform: rotate(180deg);
}



.coin-small {
  width: 26px;
  height: 26px;
  margin-right: 4px;
}
/* --- Actions & Loading --- */
.actions {
  margin-top: 20px;
  display: flex;
  gap: 12px;
}
.btn-wrapper {
  flex: 1;
  position: relative; /* overlay için gerekli */
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 5;
}

.loading-spinner {
  width: 28px;
  height: 28px;
  animation: spin 1s linear infinite; /* dilersen kaldır */
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.actions button {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: transform 0.1s;
}
.btn-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
}
.actions .long { 
  background: var(--buttongreen); 
  box-shadow: rgb(14 62 20) 0px -3px 0px 0px inset, rgb(47, 255, 57) 0px 2px 0px 0px inset;
  color: #000; 
}
.actions .short { background: var(--button2); color: #fff; }
.actions button:hover { transform: scale(1.03); }

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 5;
}
.coin-spinner {
  width: 36px;
  height: 36px;
  animation: spinCoin 1s linear infinite;
}
@keyframes spinCoin {
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
}

/* --- Linear progress --- */
.linear-progress {
  position: relative;
  background: #28285a;
  height: 12px;
  border-radius: 8px;
  margin-top: 16px;
}
.linear-progress .bar {
  background: var(--bar-inner);
  height: 100%;
  border-radius: 8px;
  transition: width 1s linear;
}
.linear-progress .linear-text {
  position: absolute;
  top: 100%;          /* barın hemen altına */
  left: 50%;          /* yatayda ortala */
  transform: translateX(-50%); /* tam ortalamak için */
  margin-top: 6px;    /* bar ile arasında biraz boşluk */
  font-size: 13px;
  color: #fbbf24;
  font-weight: bold;
  white-space: nowrap;
}

/* --- Modal --- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background-attachment: fixed;
    background: var(--background);
   
 
  padding: 24px;
  border-radius: 16px;
  width: 340px;
  text-align: center;
  color: #f9fafb;
  box-shadow: 0 6px 20px rgba(0,0,0,0.4);
}
.modal h2 {
  margin-bottom: 12px;
  font-size: 20px;
}
.details p { margin: 6px 0; font-size: 14px; }
.circle-wrapper {
  position: relative;
  width: 150px;
  height: 150px;
  margin: 20px auto;
}
svg { width: 100%; height: 100%; transform: rotate(-90deg); }
.bg {
  fill: none;
  stroke: #28285a;   /* outer bar rengi */
  stroke-width: 2.8;
}

.progress {
  fill: none;
  stroke: url(#circleGradient); /* gradient kullan */
  stroke-width: 2.8;
  stroke-linecap: round;
  transition: stroke-dasharray 1s linear;
}
.countdown-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%,-50%);
  font-size: 22px;
  font-weight: bold;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 16px 0 24px;
  text-align: left;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255,255,255,0.05);
  padding: 8px 12px;
  border-radius: 8px;
}

.detail-item .label {
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
}

.detail-item .value {
  font-size: 14px;
  font-weight: 700;
  color: #f9fafb;
  display: flex;
  align-items: center;
  gap: 6px;
}

.detail-item img {
  width: 20px;
  height: 20px;
}
.result { font-size: 18px; margin-top: 12px; }
.result.win { color: #22c55e; }
.result.lose { color: #ef4444; }
.result.draw { color: #f59e0b; }
.btn-close-modal {
  margin-top: 15px;
  background: #374151;
  color: #fff;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
}
</style>
